import React, { FC, useEffect, useState } from "react";
import classNames from "classnames";
import { graphql, useStaticQuery, Link } from "gatsby";
import { Search, Menu, Close } from "kaleidoscope/src/global/icons";
import { IconButton, TooltipPosition } from "kaleidoscope/src";
import useMainContext from "../../hooks/useMainContext";
import Label, { LabelTone } from "../../components/Label";
import { handleMouseUpByBlurring } from "kaleidoscope/src/utils/focus";
import { getFirstPostsByCategory } from "../../utils/posts";
import { kebabCase } from "lodash";
import { ReactComponent as NavBarLogo } from "../../assets/logo.svg";
import useWindowSize from "../../hooks/useWindowSize";

interface NavbarProps {
  className?: string;
  toggleSearch: () => void;
}

const Navbar: FC<NavbarProps> = ({ className, toggleSearch }) => {
  const { category, sidebarOpen, setSidebarOpen, hasSidebar } = useMainContext();
  const [searchTooltip, setSearchTooltip] = useState("Search");
  const { width } = useWindowSize();
  const isMobile = width < 1024;

  const data = useStaticQuery(graphql`
    query NavbarQuery {
      allMdx {
        nodes {
          frontmatter {
            title
          }
          parent {
            ... on File {
              relativeDirectory
            }
          }
          slug
        }
      }
      allNavbarYaml {
        nodes {
          label
        }
      }
      allSidebarYaml {
        nodes {
          label
          categories {
            label
            pages
          }
        }
      }
    }
  `);

  // Need to do this to avoid Gatsby's SSR freaking out when we access `navigator`
  useEffect(() => {
    setSearchTooltip(`Search (${navigator.platform === "MacIntel" ? "⌘+K" : "ctrl+K"})`);
  }, []);

  const categoryLinks = getFirstPostsByCategory(data.allSidebarYaml.nodes);

  return (
    <header className={classNames("kld-navbar", className)}>
      <Link className="kld-navbar__home-link" to="/" onMouseUp={handleMouseUpByBlurring}>
        <NavBarLogo />
        <div className="kld-navbar__title">Qwilr API</div>
      </Link>
      <nav className="kld-navbar__nav">
        {data.allNavbarYaml.nodes.map(({ label }: { label: string }) => (
          <Link
            className="kld-navbar__nav-link"
            to={categoryLinks[kebabCase(label)]?.url || `/${kebabCase(label)}`}
            aria-current={kebabCase(category) === kebabCase(label)}
            onMouseUp={handleMouseUpByBlurring}
            key={label}
          >
            <Label as="span" tone={LabelTone.Auto}>
              {label}
            </Label>
          </Link>
        ))}
      </nav>
      <div className="kld-navbar__actions">
        <IconButton
          onClick={toggleSearch}
          icon={<Search />}
          tooltip={{
            content: searchTooltip,
            position: TooltipPosition.Bottom,
          }}
        />
        {isMobile && hasSidebar && (
          <IconButton
            className="kld-navbar__sidebar-toggle"
            aria-pressed={sidebarOpen}
            icon={
              <div className="kld-navbar__sidebar-toggle-icons">
                <Menu className="kld-navbar__sidebar-toggle-icon kld-navbar__sidebar-toggle-icon--menu" />
                <Close className="kld-navbar__sidebar-toggle-icon kld-navbar__sidebar-toggle-icon--close" />
              </div>
            }
            aria-label="Menu"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          />
        )}
      </div>
    </header>
  );
};

export default Navbar;
